import type { Reducer, Action } from './collections.types';

/**
 * Compose multiple reducers into a single reducer function
 * @param reducers - Reducers to compose (must be 2 or more)
 * @returns Composed reducer that applies each reducer in sequence
 */
export default function composeReducers<S, A extends Action = Action>(
  ...reducers: Reducer<S, A>[]
) {
  if (reducers.length < 2) {
    throw new Error('No need for composition');
  }
  // identity is a no-op reducer used as the initial value for reduce.
  // This makes reducer composition mathematically correct (an identity function)
  // and avoids special-casing the first reducer.
  const identity: Reducer<S, A> = state => state as S;

  return reducers.reduce<Reducer<S, A>>(
    (composed, reducer) =>
      (state: S | undefined, action: A): S => {
        const next = reducer(state, action);
        return composed(next, action);
      },
    identity
  );
}
